import { evaluateSelector } from '@coast/schemas';
import { isNil } from 'lodash';
import { useCallback, useContext } from 'react';
import { CardChannelContext } from '@shared/scenes/cards/context';
import { API } from '@shared/types/api';
import { Navigator, RouteParamMapping, useNavigator } from '@shared/util/navigation';
import { useGetChannelLazyQuery } from '../../../util/hooks';
import { useGetCardLazyQuery } from '../../cards/hooks';
import { useGetWorkflowTemplateLazyQuery } from './useGetWorkflowTemplateLazyQuery';
import { useWorkflowTemplate } from './useWorkflowTemplate';

export interface ChannelEventHandlers {
  onOpenCard: (
    card?: API.CardFragment | API.WorkflowEntity | null,
    replace?: boolean,
  ) => void | (() => void);
}
/**
 * This method requires that the CardChannelContext be provided in a
 * higher component.  If it is not you must use useGetChannelEventHandlers
 * method below
 */
export function useChannelEventHandlers(): ChannelEventHandlers {
  const channel = useContext(CardChannelContext);
  const navigator = useNavigator();
  const { workflowTemplate } = useWorkflowTemplate(channel?.workflowTemplateId);

  const onOpenCard = (card?: API.CardFragment | API.WorkflowEntity | null, replace?: boolean) => {
    if (isNil(channel) || isNil(card)) {
      return;
    }
    openCard({
      cardId: card.id,
      channelSlug: channel.slug,
      selectorFields: card.fields,
      navigator,
      replace,
      behavior: workflowTemplate?.behavior,
    });
  };

  return { onOpenCard };
}

export function useGetChannelEventHandlers(): (
  cardId: string,
) => Promise<ChannelEventHandlers & { foundCard: API.CardFragment | null }> {
  const navigator = useNavigator();
  const getChannel = useGetChannelLazyQuery();
  const getCard = useGetCardLazyQuery();
  const getWorkflowTemplate = useGetWorkflowTemplateLazyQuery();
  return useCallback(
    async (cardId: string) => {
      const card = await getCard(cardId);

      if (!isNil(card)) {
        const channel = await getChannel({ id: card.channelId });
        const workflowTemplate = !isNil(card.workflowTemplateId)
          ? await getWorkflowTemplate(card.workflowTemplateId)
          : null;

        const onOpenCard = (cardToOpen?: API.CardFragment | API.WorkflowEntity | null) => {
          if (isNil(channel) || isNil(cardToOpen)) {
            return;
          }
          openCard({
            cardId: cardToOpen.id,
            channelSlug: channel.slug,
            selectorFields: cardToOpen.fields ?? {},
            navigator,
            behavior: workflowTemplate?.behavior,
          });
        };

        return { onOpenCard, foundCard: card };
      }
      return { onOpenCard: () => {}, foundCard: null };
    },
    [getCard, getChannel, getWorkflowTemplate, navigator],
  );
}

export function openCard({
  cardId,
  channelSlug,
  selectorFields,
  navigator,
  replace = false,
  behavior,
}: {
  cardId: API.CardFragment['id'];
  channelSlug: API.ChannelListItemFragment['slug'];
  selectorFields: API.CardFragment['fields'];
  navigator: Navigator;
  replace?: boolean;
  behavior?: API.WorkflowTemplateBehavior | null;
}) {
  const templateIdSelectors = behavior?.onOpenCard?.selectors;
  const navOptions: RouteParamMapping['CardUpdate'] = {
    cardId,
    channelSlug,
  };
  if (!isNil(templateIdSelectors)) {
    const templateId = templateIdSelectors.find(viewTemplateIdSelector =>
      evaluateSelector(viewTemplateIdSelector.selector, selectorFields ?? {}),
    )?.viewTemplateId;
    if (!isNil(templateId)) {
      navOptions.templateSlug = templateId;
    }
  }
  if (replace) {
    navigator.replace('CardUpdate', navOptions);
  } else {
    navigator.navigate('CardUpdate', navOptions);
  }
}
